import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {UrlQueryService} from '../../../../common/url-query/url-query.service';
import {MetaDataService} from '../../../../metaData/meta-data.service';
import * as _ from 'lodash';
import {NodeFilterService} from '../../../../../network/utils/filters/nodes/node-filter.service';
import {map} from 'rxjs/operators';
import {UserPrivilegeService} from '../../../../common/user-privilege/user-privilege.service';
import {OperatorsSettingsApiService} from '../../../settings/administration/operators/operators-settings-api.service';
import {RoutingServerGroupApiService} from '../../../settings/routing-servers/routing-server-groups/routing-server-group-api.service';
import {CredentialsSettingsApiService} from '../../../settings/administration/credentials/credentials-settings-api.service';
import {FileSaverService} from 'ngx-filesaver';
import {MatDialog} from '@angular/material/dialog';
import {FailedActionDialogComponent} from '../../../../../dialogs/failed-action-dialog/failed-action-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class NodeApiService {

  constructor(private http: HttpClient,
              private metaDataService: MetaDataService,
              private userPrivilege: UserPrivilegeService,
              private routingServerGroupApiService: RoutingServerGroupApiService,
              private operatorApiService: OperatorsSettingsApiService,
              private credentialsApiService: CredentialsSettingsApiService,
              private fileSaverService: FileSaverService,
              public dialog: MatDialog) {}

  readonly nodesUrl: string = 'topology/node';

  public getNodesForMap(params: any = {}): Promise<any> {
    return this.http.get(this.nodesUrl)
      .pipe(
        map((nodes) => {
          return this.filterNodes(nodes, params);
        })
      ).toPromise();
  }

  public getNodesForMapNew(params: any = {}, allNodes): any {
    const localNodes = _.clone(allNodes);
    return this.filterNodes(localNodes, params);
  }

  public getNode(id: number): Promise<any> {
    return this.http.get(`${this.nodesUrl}/${id}`).toPromise();
  }

  public getNodes(params: any = {}): Promise<any> {
    params = UrlQueryService.restructureFilterParams(params);
    const url = UrlQueryService.addQueryParameters(this.nodesUrl,  params);
    return this.http.get(url).toPromise();
  }

  public syncNode(id: number): Promise<any> {
    const url =  `${this.nodesUrl}/${id}/sync`;
    return this.http.get(url).toPromise();
  }

  public moveNodes(nodes: any): Promise<any> {
    const url = `${this.nodesUrl}/location`;
    return this.http.put(url, nodes).toPromise();
  }

  public editNode(node: any, id: number): Promise<any> {
    const url = `${this.nodesUrl}/${id}`;
    return this.http.put(url, node).toPromise();
  }

  public addNode(node: any): Promise<any> {
    return this.http.post(this.nodesUrl, node).toPromise();
  }

  public lockNode(id: number): Promise<any> {
    const url = `${this.nodesUrl}/${id}/lock`;
    return this.http.put(url, {lock: 1}).toPromise();
  }

  public unlockNode(id: number): Promise<any> {
    const url = `${this.nodesUrl}/${id}/lock`;
    return this.http.put(url, {lock: 0}).toPromise();
  }

  public lockNodes(ids): Promise<any> {
    const uri = `${this.nodesUrl}/lock`;
    const url = UrlQueryService.addQueryParameters(uri, {filter: {id: {value: ids, operator: '='}}});

    return this.http.put(url, {lock: this.metaDataService.PeerConnectionAdminStatus.LOCKED.value}).toPromise();
  }

  public unlockNodes(ids): Promise<any> {
    const uri = `${this.nodesUrl}/lock`;
    const url = UrlQueryService.addQueryParameters(uri, {filter: {id: {value: ids, operator: '='}}});

    return this.http.put(url, {lock: this.metaDataService.PeerConnectionAdminStatus.UNLOCKED.value}).toPromise();
  }

  public deleteNode(id): Promise<any> {
    const url = `${this.nodesUrl}/${id}`;
    return this.http.delete(url).toPromise();
  }

  public configureNode(id): Promise<any> {
    const url = `${this.nodesUrl}/${id}/configure`;
    return this.http.get(url).toPromise();
  }

  public prepareNodeForAdd(node): Promise<any> {
    const isAuthorized = this.userPrivilege.isAuthorized();

    return Promise.all([this.routingServerGroupApiService.getRoutingServerGroups().toPromise(),
      isAuthorized ? this.operatorApiService.getOperators({
        filter: {type: {
            operator: '=',
            value: ['DEVICE']}
        }}).toPromise() : [],
      isAuthorized ? this.credentialsApiService.getCredentials('DEVICE', {}).toPromise() : []]).then(value => {

      const dataObject = {
        node,
        routingServerGroups: value[0],
        nodeUsers: value[1],
        nodeCredentials: value[2]
      };

      return dataObject;
    });
  }

  public prepareNodeForEdit(id: number): Promise<any> {
    const isAuthorized = this.userPrivilege.isAuthorized();

    return Promise.all([this.getNode(id),
      this.routingServerGroupApiService.getRoutingServerGroups().toPromise(),
      isAuthorized ? this.operatorApiService.getOperators({
        filter: {type: {
            operator: '=',
            value: ['DEVICE']}
        }}).toPromise() : [],
      isAuthorized ? this.credentialsApiService.getCredentials('DEVICE', {}).toPromise() : []]).then(value => {

      const dataObject = {
        node: value[0],
        routingServerGroups: value[1],
        nodeUsers: value[2],
        nodeCredentials: value[3]
      };

      return dataObject;
    });
  }

  public getNodeINIFile(id: number): void {
    this.http.get(`${this.nodesUrl}/${id}/ini`, {responseType: 'blob', observe: 'response'}).subscribe(data => {
      const fileType = {type: 'application/octet-stream'};

      const contentDisposition = data.headers.get('Content-Disposition');
      let fileName = `${id}.ini`;

      if (contentDisposition) {
        const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = fileNameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }
      const blob = new Blob([data.body], fileType);
      this.fileSaverService.save(blob, fileName);
    }, error => {
      this.dialog.open(FailedActionDialogComponent, {
        width: '460px',
        data: {
          responseMessage: 'Failed saving INI File, make sure that the node is reachable by the Configurator',
          defaultMessage: ''
        }
      });
    });
  }

  private filterNodes(nodes, params): any {
    return (params && params.mapFilter) ?
      NodeFilterService.mapFilter(nodes, params.searchObject) : nodes;
  }
}
